import { addMonths } from 'date-fns'
import { IS_PROD } from './constant'

export type Product = {
  id: string
  coin: number
  price: number
  title?: string
  isAnnual?: boolean
  bestValue?: boolean
  isSubscription?: boolean
  tier?: 't1' | 't2' | 't3' | 't4' | 't5'
  features?: string[]
  totalPrice?: number
  recommended?: boolean
  icon?: string
  hidden?: boolean
  originalPrice?: number
}

// Legacy products - kept for reference but not exposed
const LEGACY_NON_SUBSCRIPTION_PRODUCTS: Product[] = [
  {
    id: 'heyboss_coin_pack_1',
    coin: 400,
    price: 2000,
    tier: 't3',
    hidden: true,
  },
  {
    id: 'heyboss_coin_pack_4',
    coin: 600,
    price: 3000,
    tier: 't3',
    hidden: true,
  },
  {
    id: 'heyboss_coin_pack_2',
    coin: 1000,
    price: 4900,
    originalPrice: 5000,
    tier: 't4',
    hidden: true,
  },
  {
    id: 'heyboss_coin_pack_3',
    coin: 2000,
    price: 9500,
    originalPrice: 10000,
    tier: 't5',
    hidden: true,
  },
  {
    id: 'heyboss_coin_pack_5', // HeyBoss 500 Credits
    coin: 500,
    price: 2500,
    originalPrice: 2500,
    tier: 't3',
    hidden: true,
  },
  {
    id: 'heyboss_coin_pack_6', // HeyBoss 1000 Credits
    coin: 1000,
    price: 4500,
    originalPrice: 4500,
    tier: 't4',
    hidden: true,
  },
  {
    id: 'heyboss_coin_pack_7', // HeyBoss 3000 Credits
    coin: 3000,
    price: 12000,
    originalPrice: 12000,
    tier: 't5',
    hidden: true,
  },
]

// Add-on products are no longer sold - export empty array for backward compatibility
export const NON_SUBSCRIPTION_PRODUCTS: Product[] = []

export const SUBSCRIPTION_PRODUCTS: Product[] = [
  {
    id: 'heyboss_coin_pack_1_monthly',
    title: 'Starter',
    coin: 400,
    price: 2000,
    totalPrice: 2000,
    isSubscription: true,
    tier: 't1',
    features: [
      '20 monthly usage credit',
      'Unlimited private projects',
      'Member support',
    ],
    icon: 'https://heyboss.heeyo.ai/starter.svg',
    hidden: true,
  },
  {
    id: 'heyboss_coin_pack_2_monthly',
    title: 'Growth',
    coin: 1000,
    price: 4800,
    totalPrice: 4800,
    originalPrice: 5000,
    isSubscription: true,
    tier: 't1',
    features: [
      '50 monthly usage credit',
      'Unlimited private projects',
      'Premium support',
    ],
    recommended: true,
    icon: 'https://heyboss.heeyo.ai/growth.svg',
    hidden: false,
  },
  {
    id: 'heyboss_coin_pack_3_monthly',
    title: 'Scale',
    coin: 2000,
    price: 9000,
    totalPrice: 9000,
    originalPrice: 10000,
    isSubscription: true,
    tier: 't1',
    features: [
      '100 monthly usage credit',
      'Unlimited private projects',
      'Support thread with CEO directly',
      'Early access to new features',
    ],
    icon: 'https://heyboss.heeyo.ai/scale.svg',
    hidden: false,
  },
  {
    id: 'heyboss_coin_pack_4_monthly',
    title: 'Starter',
    coin: 600,
    price: 3000,
    totalPrice: 3000,
    isSubscription: true,
    tier: 't1',
    features: [
      '30 monthly usage credit',
      'Unlimited private projects',
      'Member support',
    ],
    icon: 'https://heyboss.heeyo.ai/starter.svg',
    hidden: false,
  },
  {
    id: 'heyboss_coin_pack_1_yearly',
    title: 'Starter',
    coin: 400, // per month
    price: formatPrice((2000 * 10) / 12), // per month
    totalPrice: 2000 * 10,
    isAnnual: true,
    isSubscription: true,
    tier: 't2',
    features: [
      '20 monthly usage credit',
      'Unlimited private projects',
      'Member support',
    ],
    icon: 'https://heyboss.heeyo.ai/starter.svg',
    hidden: true,
  },
  {
    id: 'heyboss_coin_pack_2_yearly',
    title: 'Growth',
    coin: 1000, // per month
    price: formatPrice((4800 * 10) / 12), // per month
    totalPrice: 4800 * 10,
    isAnnual: true,
    isSubscription: true,
    tier: 't2',
    features: [
      '50 monthly usage credit',
      'Unlimited private projects',
      'Premium support',
    ],
    recommended: true,
    icon: 'https://heyboss.heeyo.ai/growth.svg',
    hidden: false,
  },
  {
    id: 'heyboss_coin_pack_3_yearly',
    title: 'Scale',
    coin: 2000, // per month
    price: formatPrice((9000 * 10) / 12), // per month
    totalPrice: 9000 * 10,
    isAnnual: true,
    isSubscription: true,
    tier: 't2',
    features: [
      '100 monthly usage credit',
      'Unlimited private projects',
      'Support thread with CEO directly',
      'Early access to new features',
    ],
    icon: 'https://heyboss.heeyo.ai/scale.svg',
    hidden: false,
  },
  {
    id: 'heyboss_coin_pack_4_yearly',
    title: 'Starter',
    coin: 600,
    price: formatPrice((3000 * 10) / 12),
    totalPrice: 3000 * 10,
    isAnnual: true,
    isSubscription: true,
    tier: 't2',
    features: [
      '30 monthly usage credit',
      'Unlimited private projects',
      'Member support',
    ],
    icon: 'https://heyboss.heeyo.ai/starter.svg',
    hidden: false,
  },
]

export const ALL_PRODUCTS = [
  ...LEGACY_NON_SUBSCRIPTION_PRODUCTS,
  ...SUBSCRIPTION_PRODUCTS,
]

export const getProductById = (id: string) => {
  return ALL_PRODUCTS.find((product) => product.id === id)
}

export const STRIPE_PAYMENT_LINKS = [
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_ID_COIN_PACK_1,
    link: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_COIN_PACK_1,
    product_id: 'heyboss_coin_pack_1',
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_ID_COIN_PACK_2,
    link: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_COIN_PACK_2,
    product_id: 'heyboss_coin_pack_2',
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_ID_COIN_PACK_3,
    link: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_COIN_PACK_3,
    product_id: 'heyboss_coin_pack_3',
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_ID_COIN_PACK_4,
    link: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_COIN_PACK_4,
    product_id: 'heyboss_coin_pack_4',
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_ID_COIN_PACK_1_MONTHLY,
    link: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_COIN_PACK_1_MONTHLY,
    product_id: 'heyboss_coin_pack_1_monthly',
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_ID_COIN_PACK_1_YEARLY,
    link: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_COIN_PACK_1_YEARLY,
    product_id: 'heyboss_coin_pack_1_yearly',
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_ID_COIN_PACK_2_MONTHLY,
    link: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_COIN_PACK_2_MONTHLY,
    product_id: 'heyboss_coin_pack_2_monthly',
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_ID_COIN_PACK_2_YEARLY,
    link: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_COIN_PACK_2_YEARLY,
    product_id: 'heyboss_coin_pack_2_yearly',
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_ID_COIN_PACK_3_MONTHLY,
    link: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_COIN_PACK_3_MONTHLY,
    product_id: 'heyboss_coin_pack_3_monthly',
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_ID_COIN_PACK_3_YEARLY,
    link: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_COIN_PACK_3_YEARLY,
    product_id: 'heyboss_coin_pack_3_yearly',
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_ID_COIN_PACK_4_MONTHLY,
    link: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_COIN_PACK_4_MONTHLY,
    product_id: 'heyboss_coin_pack_4_monthly',
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_ID_COIN_PACK_4_YEARLY,
    link: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK_COIN_PACK_4_YEARLY,
    product_id: 'heyboss_coin_pack_4_yearly',
  },
]

export const getProductIdByStripePaymentLinkId = (id: string) => {
  return STRIPE_PAYMENT_LINKS.find((p) => p.id === id)?.product_id
}

export const getStripePaymentLinkByProductId = (id: string) => {
  return STRIPE_PAYMENT_LINKS.find((p) => p.product_id === id)?.link
}

export function getNextSendTime(ms: number) {
  return addMonths(ms, 1).getTime()
}

export const PRODUCT_PRICES = [
  {
    product_id: 'heyboss_coin_pack_1',
    price_id: IS_PROD
      ? 'price_1Qg7SQKyH788hRW0odAu3qS9'
      : 'price_1Qe7lOKyH788hRW0XVdgtUay',
  },
  {
    product_id: 'heyboss_coin_pack_2',
    price_id: IS_PROD
      ? 'price_1RGAqkKyH788hRW04jXcMVdG'
      : 'price_1REOjcKyH788hRW00S3E2BeH',
  },
  {
    product_id: 'heyboss_coin_pack_3',
    price_id: IS_PROD
      ? 'price_1RGAsFKyH788hRW0JWZTN0KQ'
      : 'price_1REOtoKyH788hRW0iET6Uh0h',
  },
  {
    product_id: 'heyboss_coin_pack_4',
    price_id: IS_PROD
      ? 'price_1REO5oKyH788hRW0THWdIjIF'
      : 'price_1RE2LFKyH788hRW0RdymL16u',
  },
  {
    product_id: 'heyboss_coin_pack_5',
    price_id: IS_PROD
      ? 'price_1RRUdFKyH788hRW0RxM0awID'
      : 'price_1RROP2KyH788hRW0d9el0tI5',
  },
  {
    product_id: 'heyboss_coin_pack_6',
    price_id: IS_PROD
      ? 'price_1RRUdIKyH788hRW0N2c0LmxI'
      : 'price_1RROSBKyH788hRW0AUPac4gU',
  },
  {
    product_id: 'heyboss_coin_pack_7',
    price_id: IS_PROD
      ? 'price_1RRUdMKyH788hRW0kGaOITxI'
      : 'price_1RROSYKyH788hRW0HaJLfJWp',
  },
  {
    product_id: 'heyboss_coin_pack_1_monthly',
    price_id: IS_PROD
      ? 'price_1Qg7SfKyH788hRW0koCgnHHe'
      : 'price_1Qg6LuKyH788hRW0jlyk10t6',
  },
  {
    product_id: 'heyboss_coin_pack_1_yearly',
    price_id: IS_PROD
      ? 'price_1Qg7SlKyH788hRW0aktuJvOu'
      : 'price_1Qg6MIKyH788hRW03u3ruvWA',
  },
  {
    product_id: 'heyboss_coin_pack_2_monthly',
    price_id: IS_PROD
      ? 'price_1REQFiKyH788hRW0MQPbvAf3'
      : 'price_1REOnXKyH788hRW0PHuWlHN1',
  },
  {
    product_id: 'heyboss_coin_pack_2_yearly',
    price_id: IS_PROD
      ? 'price_1REQFaKyH788hRW0CihkdV9t'
      : 'price_1REQ9qKyH788hRW0L9jNQPWT',
  },
  {
    product_id: 'heyboss_coin_pack_3_monthly',
    price_id: IS_PROD
      ? 'price_1REQF0KyH788hRW0mNLHikXM'
      : 'price_1REOtDKyH788hRW0uo1g4FZh',
  },
  {
    product_id: 'heyboss_coin_pack_3_yearly',
    price_id: IS_PROD
      ? 'price_1REQErKyH788hRW0bNxfjeDX'
      : 'price_1REQB9KyH788hRW0VJqudkVE',
  },
  {
    product_id: 'heyboss_coin_pack_4_monthly',
    price_id: IS_PROD
      ? 'price_1RE2KiKyH788hRW0Z06JeqIp'
      : 'price_1RE2LOKyH788hRW0z518zagc',
  },
  {
    product_id: 'heyboss_coin_pack_4_yearly',
    price_id: IS_PROD
      ? 'price_1RE2KtKyH788hRW09FJgWIwf'
      : 'price_1REMyWKyH788hRW0PRQuXTSI',
  },
]

export function getPriceIdByProductId(productId: string) {
  return PRODUCT_PRICES.find((p) => p.product_id === productId)?.price_id
}

function formatPrice(price: number) {
  return Number(price.toFixed(0))
}

// Project subscription prices (in cents)
export const PROJECT_BASIC_MONTHLY_PRICE = 2499 // $24.99
export const PROJECT_BASIC_YEARLY_PRICE = 24000 // 20% discount
export const PROJECT_PREMIUM_MONTHLY_PRICE = 4999 // $49.99
export const PROJECT_PREMIUM_YEARLY_PRICE = 48000 // 20% discount
export const PROJECT_MEGA_ANNUAL_PRICE = 16900 // $169/year - Special launch offer

export const PROJECT_SUBSCRIPTIONS_PRICES = {
  basic_monthly: PROJECT_BASIC_MONTHLY_PRICE,
  basic_yearly: PROJECT_BASIC_YEARLY_PRICE,
  premium_monthly: PROJECT_PREMIUM_MONTHLY_PRICE,
  premium_yearly: PROJECT_PREMIUM_YEARLY_PRICE,
  mega_annual: PROJECT_MEGA_ANNUAL_PRICE,
}

export function getProjectSubscriptionPrice(
  plan: string,
  billingCycle: 'monthly' | 'yearly',
) {
  const key =
    `${plan}_${billingCycle}` as keyof typeof PROJECT_SUBSCRIPTIONS_PRICES
  return PROJECT_SUBSCRIPTIONS_PRICES[key]
}

// Price IDs from Stripe Dashboard
export const PROJECT_SUBSCRIPTION_PRICE_IDS = {
  basic_monthly: IS_PROD
    ? 'price_1RbcdoKyH788hRW0uwqUb7Xy'
    : 'price_1RbbnwKyH788hRW0B8O7dROw',
  basic_yearly: IS_PROD
    ? 'price_1RbceTKyH788hRW0XsI3KQzn'
    : 'price_1RbbojKyH788hRW06WkeuHB2',
  premium_monthly: IS_PROD
    ? 'price_1Rbcf8KyH788hRW0ggaab9ZO'
    : 'price_1RbbwbKyH788hRW0uLWK1bDN',
  premium_yearly: IS_PROD
    ? 'price_1RbcfUKyH788hRW0V8aodE6J'
    : 'price_1Rbc3WKyH788hRW04i9uzZml',
  // TODO: Replace with actual Stripe Price IDs after creating product
  mega_annual: IS_PROD
    ? 'price_XXXXXXXXXX' // Replace with production Price ID
    : 'price_YYYYYYYYYY', // Replace with test Price ID
}

// Helper function to get price ID based on plan and billing cycle
export function getProjectSubscriptionPriceId(
  plan: string,
  billingCycle: 'monthly' | 'yearly',
) {
  const key =
    `${plan}_${billingCycle}` as keyof typeof PROJECT_SUBSCRIPTION_PRICE_IDS
  return PROJECT_SUBSCRIPTION_PRICE_IDS[key]
}

// Project plan to coin mapping (configurable)
export const PROJECT_PLAN_COIN: Record<string, number> = {
  basic: 200,
  premium: 500,
  mega_annual: 10000, // 10,000 credits for mega annual plan
}

export function getProjectPlanCoin(plan: string): number {
  return PROJECT_PLAN_COIN[plan] || 0
}
